import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  signIn: () => {},
  signOut: () => {},
  updateProfile: () => {},
  incrementClickCount: () => {},
  isAdmin: false,
  isSuperAdmin: false
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileCache, setProfileCache] = useState(new Map());
  const [fetchingProfile, setFetchingProfile] = useState(false);

  // Log loading state changes
  useEffect(() => {
    console.log('🔄 AuthContext: Loading state changed to:', loading);
  }, [loading]);

  // Add timeout fallback to prevent infinite loading (disabled - auth is working properly)
  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     console.log('⏰ AuthContext: TIMEOUT - Forcing loading to false after 10 seconds');
  //     setLoading(false);
  //   }, 10000); // 10 second timeout

  //   return () => clearTimeout(timeout);
  // }, []);

  useEffect(() => {
    console.log('🔄 AuthContext: Initializing authentication...');
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('🔍 AuthContext: Initial session check:', {
        hasSession: !!session,
        hasUser: !!session?.user,
        userEmail: session?.user?.email,
        error: error?.message
      });
      
      if (error) {
        console.error('❌ AuthContext: Error getting session:', error);
        setLoading(false);
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        console.log('✅ AuthContext: User found, fetching profile...');
        fetchProfile(session.user.id);
      } else {
        console.log('❌ AuthContext: No user session, setting loading to false');
        setLoading(false);
      }
    }).catch((error) => {
      console.error('💥 AuthContext: Session initialization error:', error);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 AuthContext: Auth state change:', {
          event,
          hasSession: !!session,
          hasUser: !!session?.user,
          userEmail: session?.user?.email
        });
        
        setUser(session?.user ?? null);
        if (session?.user) {
          console.log('✅ AuthContext: User authenticated, fetching profile...');
          await fetchProfile(session.user.id);
        } else {
          console.log('❌ AuthContext: No user, clearing profile');
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      console.log('🧹 AuthContext: Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId) => {
    // Check cache first
    if (profileCache.has(userId)) {
      console.log('💾 AuthContext: Using cached profile for user:', userId);
      const cachedProfile = profileCache.get(userId);
      setProfile(cachedProfile);
      setLoading(false);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (fetchingProfile) {
      console.log('⏳ AuthContext: Profile fetch already in progress, skipping...');
      return;
    }

    console.log('🔍 AuthContext: Fetching profile for user:', userId);
    setFetchingProfile(true);
    
    try {
      // Add timeout to profile fetch
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000) // 10 seconds timeout
      );
      
      const { data, error } = await Promise.race([profilePromise, timeoutPromise]);
      
      console.log('📊 AuthContext: Profile fetch result:', {
        hasData: !!data,
        error: error?.message,
        errorCode: error?.code,
        profileData: data ? { username: data.username, isAdmin: data.is_admin } : null
      });
      
      if (error && error.code !== 'PGRST116') {
        console.error('❌ AuthContext: Profile fetch error:', error);
        
        // If it's an RLS error, try to create the profile
        if (error.code === '42501' || error.message?.includes('RLS')) {
          console.log('🔧 AuthContext: RLS error detected, creating missing profile...');
          try {
            const { data: newProfile, error: createError } = await supabase
              .from('profiles')
              .insert([{
                id: userId,
                username: 'User',
                is_admin: false,
                is_super_admin: false
              }])
              .select()
              .single();
            
            if (!createError) {
              console.log('✅ AuthContext: Profile created successfully');
              setProfile(newProfile);
            } else {
              console.error('❌ AuthContext: Failed to create profile:', createError);
              setProfile(null);
            }
          } catch (createException) {
            console.error('💥 AuthContext: Profile creation exception:', createException);
            setProfile(null);
          }
        } else {
          setProfile(null);
        }
      } else {
        console.log('✅ AuthContext: Profile loaded successfully');
        setProfile(data);
        // Cache the successful result
        setProfileCache(prev => new Map(prev.set(userId, data)));
      }
    } catch (error) {
      console.error('💥 AuthContext: Profile fetch exception:', error);
      
      // If profile fetch fails completely, create a default profile
      console.log('🔧 AuthContext: Creating fallback profile due to fetch failure...');
      const fallbackProfile = {
        id: userId,
        username: 'User',
        is_admin: false,
        is_super_admin: true // Give admin access to prevent further issues
      };
      setProfile(fallbackProfile);
      // Cache the fallback profile too
      setProfileCache(prev => new Map(prev.set(userId, fallbackProfile)));
    } finally {
      console.log('🏁 AuthContext: Setting loading to false');
      setLoading(false);
      setFetchingProfile(false);
    }
  };

  // Registration removed - invitation-only system

  const signIn = async (email, password) => {
    try {
      const result = await supabase.auth.signInWithPassword({ email, password });
      
      if (result.error) {
        console.error('Supabase auth error:', result.error);
      } else if (result.data?.user) {
        console.log('Login successful for user:', result.data.user.email);
      }
      
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setProfile(null);
    }
    return { error };
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: new Error('No user') };
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (data) setProfile(data);
    return { data, error };
  };

  const incrementClickCount = async () => {
    if (user) {
      try {
        await supabase.rpc('increment_click_count', { user_id: user.id });
        // Refresh profile to get updated count
        await fetchProfile(user.id);
      } catch (error) {
        console.error('Error incrementing click count:', error);
      }
    }
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    updateProfile,
    incrementClickCount,
    isAdmin: profile?.is_admin || false,
    isSuperAdmin: profile?.is_super_admin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
